import {useBreakPoint, useModal} from '@hooks';
import {ModalPage, ModalPageBottom} from '@organisms';
import {useTranslation} from 'react-i18next';
import {
  Group,
  actions,
  useAppDispatch,
  withAsyncThunkErrorHandling,
  useAppSelector,
  searchGroupMembersResults,
} from '@lib/store';
import {useCallback, useState} from 'react';
import {toast} from 'react-hot-toast';
import {H4} from '@quarks';
import {GroupAvatarDefinition} from '@lib/logic';
import {GroupMemberSelector} from './GroupMemberSelector';
import {PersonLike} from '@lib/infrastructure';

type Props =
  | {
      groupTitle: string;
      groupAvatar: GroupAvatarDefinition | null;
      group?: never;
    }
  | {
      group: Group;
      groupTitle?: never;
      groupAvatar?: never;
    };

export const GroupMemberSelectionModal = ({group, groupAvatar, groupTitle}: Props) => {
  const {t} = useTranslation();
  const {closeModal, removePages, modalPages} = useModal();
  const dispatch = useAppDispatch();

  const isEditMode = !!group;
  const currentUserId = useAppSelector((state) => state.data.user.user.id);
  const searchResults = useAppSelector(searchGroupMembersResults);

  const [selectedMembers, setSelectedMembers] = useState<PersonLike[]>([]);

  const handleCreate = useCallback(async () => {
    if (!groupTitle || !groupAvatar) return;

    const result = await dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.createGroup({
          name: groupTitle,
          description: undefined,
          emoji: groupAvatar.emoji,
          color: groupAvatar.color,
          imageUrl: undefined,
          memberUserIds: [currentUserId, ...selectedMembers.map((i) => i.id)],
        }),
      ),
    );

    if (result.success) {
      toast.success(t('groups:GroupCreatedToast'));
      closeModal();
    }
  }, [dispatch, groupTitle, groupAvatar, currentUserId, selectedMembers, t, closeModal]);

  const handleEdit = useCallback(async () => {
    if (!group) return;

    const currentMemberIds = group.members.filter((m) => m.status !== 'Removed').map((m) => m.user.id);
    const selectedMemberIds = selectedMembers.map((m) => m.id);

    const result = await dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.updateGroup({
          groupId: group.id,
          dto: {
            name: group.name,
            memberUserIds: [...currentMemberIds, ...selectedMemberIds],
            imageUrl: group.imageUrl,
            emoji: group.emoji,
            color: group.color,
          },
        }),
      ),
    );

    if (result.success) {
      toast.success(t('groups:GroupUpdatedToast'));
      closeModal();
    }
  }, [dispatch, group, selectedMembers, t, closeModal]);

  const handleSave = isEditMode ? handleEdit : handleCreate;

  const breakpoint = useBreakPoint();
  const handleClick = () => (isEditMode ? closeModal() : removePages(1));

  return (
    <ModalPage
      onBack={modalPages.length > 1 ? () => removePages(1) : undefined}
      bottom={
        <ModalPageBottom
          buttonLabel={isEditMode ? t('translation:Save') : t('translation:Done')}
          cancelButtonLabel={isEditMode ? t('translation:Cancel') : t('translation:Back')}
          onCancel={() => handleClick()}
          onClick={handleSave}
        />
      }
      onClose={closeModal}
      title={t('groups:AddMembersLabel')}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <H4 className="text-gray-900">{t('groups:AddMembersLabel')}</H4>

          <GroupMemberSelector
            value={selectedMembers}
            setValue={setSelectedMembers}
            searchResults={searchResults.data || []}
            onSearch={actions.searchGroupMembers}
            searchStatus={
              searchResults.status === 'pending' ? 'Loading' : searchResults.status === 'rejected' ? 'Failed' : 'Loaded'
            }
            disabledValues={
              isEditMode && group
                ? group.members.map((m) => ({
                    id: m.user.id,
                    reason: t(`groups:Status_${m.status}` as any),
                  }))
                : undefined
            }
          />
        </div>
      </div>
    </ModalPage>
  );
};
